import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Upload,
  Database,
  Download,
  Save,
  Loader2,
  CheckCircle2,
  AlertCircle,
  FlaskConical,
  ArrowRight,
  Trash2,
  Sparkles,
  X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function AssetImportPage() {
  const { tag } = useParams();
  const navigate = useNavigate();
  return tag ? <AssetImportDetail importTag={tag} /> : <AssetImportHub onOpen={(t) => navigate(`/asset-imports/${t}`)} />;
}

// ─── Hub ──────────────────────────────────────────────────────────────────

function AssetImportHub({ onOpen }) {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/asset-imports`, { withCredentials: true });
      setImports(r.data?.items || []);
    } catch (e) {
      toast.error('Failed to load asset imports');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-7xl mx-auto p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FlaskConical className="w-6 h-6 text-emerald-600" />
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Asset Imports
          </h1>
        </div>
        <Button onClick={() => setShowUpload(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="upload-asset-sheet-btn">
          <Upload className="w-4 h-4 mr-1" /> Upload Asset Sheet
        </Button>
      </div>
      <p className="text-xs text-slate-500 mb-5 max-w-3xl">
        Bulk-import asset-level data (Asset name · Company · Phase · Indication · MoA · Modality)
        from BiopharmaCatalyst-style exports. Each row is fuzzy-matched to an existing biotech;
        existing assets are <span className="text-emerald-700 font-medium">advanced</span> only
        (Pre-Clinical → Phase 1 → … → Commercial), never downgraded. Missing assets are{' '}
        <span className="text-emerald-700 font-medium">created</span> against the matched biotech.
      </p>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-2.5 text-left">Import</th>
              <th className="px-4 py-2.5 text-left">Status</th>
              <th className="px-4 py-2.5 text-right w-28">Total</th>
              <th className="px-4 py-2.5 text-right w-28">Matched</th>
              <th className="px-4 py-2.5 text-right w-28">Stage Δ</th>
              <th className="px-4 py-2.5 text-right w-28">Committed</th>
              <th className="px-4 py-2.5 text-right w-32">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></td></tr>
            )}
            {!loading && imports.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400 text-sm">
                No asset imports yet. Click <span className="font-semibold text-emerald-700">Upload Asset Sheet</span> to start.
              </td></tr>
            )}
            {imports.map(i => (
              <tr key={i.import_tag} className="hover:bg-slate-50/50" data-testid={`asset-import-row-${i.import_tag}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{i.job?.label || i.import_tag}</div>
                  <div className="text-[11px] text-slate-400">{i.import_tag} · {i.job?.filename || ''}</div>
                </td>
                <td className="px-4 py-3"><StageBadge stage={i.job?.stage} /></td>
                <td className="px-4 py-3 text-right tabular-nums">{i.total}</td>
                <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">{i.matched}</td>
                <td className="px-4 py-3 text-right tabular-nums text-amber-700 font-medium">{i.stage_changes}</td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-500">{i.committed}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" onClick={() => onOpen(i.import_tag)} data-testid={`open-asset-import-${i.import_tag}`} className="bg-slate-900 hover:bg-slate-800">
                    Review <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showUpload && <UploadDialog onClose={() => setShowUpload(false)} onDone={(t) => { setShowUpload(false); load(); onOpen(t); }} />}
    </div>
  );
}

function StageBadge({ stage }) {
  const map = {
    queued: ['bg-slate-100 text-slate-600', 'Queued'],
    matching: ['bg-amber-100 text-amber-700', 'Matching…'],
    ready: ['bg-emerald-100 text-emerald-700', 'Ready'],
    error: ['bg-rose-100 text-rose-700', 'Error'],
  };
  const [cls, lbl] = map[stage] || ['bg-slate-100 text-slate-600', stage || '—'];
  return <Badge className={`${cls} text-xs`}>{lbl}</Badge>;
}

function UploadDialog({ onClose, onDone }) {
  const fileRef = useRef(null);
  const [tag, setTag] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error('Pick a file'); return; }
    if (!tag.trim()) { toast.error('Pick an import_tag'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('import_tag', tag.trim());
      fd.append('label', label.trim());
      const r = await axios.post(`${API}/asset-imports/upload`, fd, { withCredentials: true });
      toast.success(`${r.data.row_count} rows queued — matching in background`);
      onDone(r.data.import_tag);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl">
        <div className="text-base font-semibold mb-3">Upload Asset Sheet</div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-600">File (.xlsx / .csv)</label>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" className="block w-full text-xs mt-1" data-testid="asset-import-file" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Import tag (lowercase, underscores)</label>
            <Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="biopharma_catalyst_2026" data-testid="asset-import-tag" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Label (optional)</label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="BiopharmaCatalyst Export Feb 2026" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="asset-import-submit">
            {busy ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Uploading…</> : 'Upload'}
          </Button>
        </div>
        <div className="text-[11px] text-slate-400 mt-3">
          Required columns: <code>Asset name</code>, <code>Company name</code>. Recommended:
          <code> Development phase</code>, <code>Asset type</code>, <code>Clinical indication(s)</code>,
          <code> Mechanisms of action</code>.
        </div>
      </form>
    </div>
  );
}

// ─── Detail ───────────────────────────────────────────────────────────────

function AssetImportDetail({ importTag }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');  // all | matched | unmatched | stage_changes | pharma_promote
  const [search, setSearch] = useState('');
  const [committing, setCommitting] = useState(false);
  const [enrich, setEnrich] = useState(null);  // { total, qualifying, job }
  const [enrichKicking, setEnrichKicking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'matched') params.match_status = 'matched';
      if (filter === 'unmatched') params.match_status = 'unmatched';
      if (filter === 'pharma_promote') params.match_status = 'pharma_promote';
      if (filter === 'stage_changes') params.stage_changed = 1;
      const r = await axios.get(`${API}/asset-imports/${importTag}/preview`, { withCredentials: true, params });
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [importTag, filter]);

  // Auto-refresh while matching
  useEffect(() => {
    if (data?.job?.stage === 'matching' || data?.job?.stage === 'queued') {
      const t = setTimeout(load, 3500);
      return () => clearTimeout(t);
    }
  }, [data]); // eslint-disable-line

  // Fetch enrichment counts whenever the import has committed assets.
  const loadEnrich = async () => {
    try {
      const r = await axios.get(`${API}/asset-imports/${importTag}/enrich-preview`, { withCredentials: true });
      setEnrich(r.data);
    } catch { /* non-fatal */ }
  };
  useEffect(() => {
    if (data?.committed > 0) loadEnrich();
    // eslint-disable-next-line
  }, [data?.committed]);

  // Poll enrichment status while running.
  useEffect(() => {
    const st = enrich?.job?.status;
    if (st === 'running' || st === 'queued') {
      const t = setTimeout(loadEnrich, 3000);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [enrich]);

  const kickEnrich = async () => {
    if (!window.confirm(
      `Run Perplexity enrichment on ${enrich?.qualifying || 0} qualifying assets?\n\n` +
      `Only clinical-stage assets (Phase 1+) missing MoA / differentiation / IP / next milestone will be enriched. ` +
      `Existing data is preserved — Perplexity only fills empty fields. This runs in the background.`
    )) return;
    setEnrichKicking(true);
    try {
      const r = await axios.post(`${API}/asset-imports/${importTag}/enrich`, {}, { withCredentials: true });
      if (r.data?.started) {
        toast.success(`Perplexity enrichment kicked off for ${r.data.qualifying} assets`);
        loadEnrich();
      } else {
        toast.info(r.data?.reason || 'Nothing to enrich');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start enrichment');
    } finally {
      setEnrichKicking(false);
    }
  };

  const cancelEnrich = async () => {
    try {
      await axios.post(`${API}/asset-imports/${importTag}/enrich-cancel`, {}, { withCredentials: true });
      toast.success('Cancellation requested — in-flight calls will finish, then the job stops.');
      loadEnrich();
    } catch { toast.error('Could not cancel'); }
  };

  const items = useMemo(() => {
    const arr = data?.items || [];
    if (!search.trim()) return arr;
    const q = search.toLowerCase();
    return arr.filter(r =>
      (r.asset_name || '').toLowerCase().includes(q) ||
      (r.company_name || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const toggleRow = async (id, approved) => {
    setData(d => ({ ...d, items: d.items.map(r => r.id === id ? { ...r, approved } : r) }));
    try {
      await axios.patch(`${API}/asset-imports/staging/${id}`, { approved }, { withCredentials: true });
    } catch {
      toast.error('Save failed');
      load();
    }
  };

  const approveAll = async () => {
    try {
      const r = await axios.post(`${API}/asset-imports/${importTag}/approve-all`, {}, { withCredentials: true });
      toast.success(`Approved ${r.data.approved} matched rows`);
      load();
    } catch (e) { toast.error('Bulk approve failed'); }
  };

  const [rematching, setRematching] = useState(false);
  const rematchUnmatched = async () => {
    // Re-runs biotech match on `unmatched` rows using the corporate-suffix
    // canonicalizer (rescues rows like "Shanghai Huaota Biopharm" ↔
    // "Shanghai Huaota Biopharm Ltd"). Auto-approves rescued rows so the
    // operator can commit immediately.
    //
    // BATCHED: the endpoint processes 150 rows/call — small enough that
    // even with the initial 100k-name-index build + fuzzy scan the
    // origin responds well under Cloudflare's 100s edge timeout. On any
    // 520 / 524 / network / JSON-parse error we retry the same batch
    // once with half the limit, then move on (idempotent — server-side
    // bulk_write only touches rows still tagged `unmatched`). Progress
    // toast updates every batch so the operator sees the rescue count
    // climb in real time.
    if (rematching) return;
    setRematching(true);
    let totalRescuedBt = 0, totalRescuedPromote = 0, batches = 0;
    // Feb 2026 v3 · run_id makes progress monotonic. Each click gets a
    // fresh UUID; server excludes rows already scanned this session so
    // batches don't loop over the same still-unmatched rows forever
    // ("Batch 37 · rescued 0 · 1611 still unmatched" bug).
    const runId = (crypto.randomUUID && crypto.randomUUID()) || `run-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const toastId = toast.loading('Re-matching unmatched rows…');

    // Small helper: hit the endpoint with retry on Cloudflare-shaped
    // errors (network / 520 / 524 / non-JSON HTML body).
    const callBatch = async (lim) => {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const r = await axios.post(
            `${API}/asset-imports/${importTag}/rematch-unmatched?limit=${lim}&run_id=${encodeURIComponent(runId)}`,
            {},
            { withCredentials: true, timeout: 110000 },
          );
          return r.data || {};
        } catch (err) {
          const status = err?.response?.status;
          const bodyIsHtml = typeof err?.response?.data === 'string'
            && err.response.data.startsWith('<');
          const looksCloudflare = !err.response || status === 520 || status === 524
            || status === 502 || status === 522 || bodyIsHtml;
          if (attempt === 0 && looksCloudflare) {
            // Half the batch size and retry once. Server bulk_write
            // is idempotent — anything it managed to rescue on the
            // first attempt is already saved.
            lim = Math.max(25, Math.floor(lim / 2));
            await new Promise(r => setTimeout(r, 1500));
            continue;
          }
          throw err;
        }
      }
    };

    try {
      // Safety cap: max 80 batches × 150 = 12k rescued rows per click.
      let currentLimit = 150;
      for (let i = 0; i < 80; i++) {
        const d = await callBatch(currentLimit);
        batches += 1;
        totalRescuedBt += (d.rescued_to_biotech || 0);
        totalRescuedPromote += (d.rescued_to_pharma_promote || 0);
        const remaining = d.totals?.unmatched ?? 0;
        toast.loading(
          `Batch ${batches} · rescued ${totalRescuedBt + totalRescuedPromote} so far · ${remaining} still unmatched…`,
          { id: toastId },
        );
        // has_more is authoritative — server tells us when the current
        // run_id has fully iterated the unmatched pool.
        if (!d.has_more) break;
        // Defensive safety: if the server returned 0 processed rows
        // (i.e. `scanned` == 0), stop — nothing more this session.
        if ((d.scanned ?? 0) === 0) break;
        // Small breather between batches so the origin isn't hammered
        // and so any transient Cloudflare rate-limit clears.
        await new Promise(r => setTimeout(r, 250));
      }
      const totalRescued = totalRescuedBt + totalRescuedPromote;
      if (totalRescued === 0) {
        toast.info('No additional matches found on this pass.', { id: toastId });
      } else {
        toast.success(
          `Rescued ${totalRescued} rows across ${batches} batch(es) · ${totalRescuedBt} → biotech · ${totalRescuedPromote} → promote`,
          { id: toastId, duration: 6000 },
        );
      }
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || 'Re-match paused — partial progress was saved. Click Re-matching again to resume from where it left off.',
        { id: toastId },
      );
    } finally {
      setRematching(false);
    }
  };

  const commit = async () => {
    if (!window.confirm(`Commit ${data?.approved} approved rows to db.assets?\n\nExisting assets will be enriched (phase advances only, never downgrades). New assets will be created.`)) return;
    setCommitting(true);
    try {
      const r = await axios.post(`${API}/asset-imports/${importTag}/commit`, {}, { withCredentials: true, timeout: 600000 });
      toast.success(`Commit done — ${r.data.created} created · ${r.data.enriched} enriched · ${r.data.skipped} skipped`);
      load();
      // Re-poll enrichment counts (newly committed rows may qualify).
      setTimeout(loadEnrich, 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Commit failed');
    } finally {
      setCommitting(false);
    }
  };

  const downloadXlsx = async (matchStatusFilter, stageChangedFilter) => {
    try {
      const params = {};
      if (matchStatusFilter) params.match_status = matchStatusFilter;
      if (stageChangedFilter) params.stage_changed = 1;
      const res = await axios.get(`${API}/asset-imports/${importTag}/export`, { withCredentials: true, params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const tag = [matchStatusFilter, stageChangedFilter && 'stage-change'].filter(Boolean).join('_') || 'all';
      a.download = `${importTag}_${tag}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  if (loading && !data) return <div className="p-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!data) return null;

  const isMatching = data.job?.stage === 'matching' || data.job?.stage === 'queued';

  return (
    <div className="max-w-7xl mx-auto p-8">
      <Link to="/asset-imports" className="text-xs text-slate-500 hover:underline">← All asset imports</Link>
      <div className="flex items-center justify-between mt-2 mb-2">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-emerald-600" />
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Asset Import — {(data.job?.label || importTag).toUpperCase()}
          </h1>
          <StageBadge stage={data.job?.stage} />
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => downloadXlsx('matched', 0)} data-testid="dl-matched"><Download className="w-3 h-3 mr-1" /> Matched.xlsx</Button>
          <Button size="sm" variant="outline" onClick={() => downloadXlsx('unmatched', 0)} data-testid="dl-unmatched"><Download className="w-3 h-3 mr-1" /> Unmatched.xlsx</Button>
          <Button size="sm" variant="outline" onClick={() => downloadXlsx('', 1)} data-testid="dl-stage-changes"><Download className="w-3 h-3 mr-1" /> Stage Δ.xlsx</Button>
          <Button size="sm" variant="outline" onClick={() => downloadXlsx('', 0)} data-testid="dl-all"><Download className="w-3 h-3 mr-1" /> Full.xlsx</Button>
        </div>
      </div>
      <p className="text-xs text-slate-500">
        <span className="font-semibold text-slate-900">{data.total}</span> rows parsed ·{' '}
        <span className="font-semibold text-emerald-700">{data.matched}</span> matched ·{' '}
        <span className="font-semibold text-rose-700">{data.unmatched}</span> unmatched ·{' '}
        <span className="font-semibold text-amber-700">{data.stage_changes}</span> stage changes ·{' '}
        <span className="font-semibold text-slate-700">{data.committed}</span> already committed
      </p>

      {isMatching && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="flex-1">
            Background matching in progress — {data.job?.matched || 0} of ~{data.total} processed so far. This page auto-refreshes.
          </span>
          <ResumeMatchingButton importTag={importTag} pending={Math.max(0, (data.total || 0) - (data.matched || 0) - (data.unmatched || 0) - (data.stage_changes || 0))} onResumed={load} />
        </div>
      )}

      {/* Show resume button even when isMatching is false, if pending rows exist —
          catches the case where the job died but the UI thinks it's done. */}
      {!isMatching && (((data.total || 0) - (data.matched || 0) - (data.unmatched || 0) - (data.stage_changes || 0)) > 0) && (
        <div className="mt-3 bg-rose-50 border border-rose-200 rounded p-3 text-xs text-rose-900 flex items-center gap-2">
          <span className="flex-1">
            ⚠ <strong>{(data.total || 0) - (data.matched || 0) - (data.unmatched || 0) - (data.stage_changes || 0)}</strong> rows are still un-matched — the background task likely died (pod restart). Click Resume to finish.
          </span>
          <ResumeMatchingButton importTag={importTag} pending={(data.total || 0) - (data.matched || 0) - (data.unmatched || 0) - (data.stage_changes || 0)} onResumed={load} />
        </div>
      )}

      {/* Perplexity Enrichment Panel — surfaces only when this import has
          committed assets. Lets the user fill missing MoA / differentiation /
          IP / next milestone on clinical-stage rows via Perplexity Sonar. */}
      {data.committed > 0 && enrich && (
        <div className="mt-3 rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50/70 to-fuchsia-50/50 p-4" data-testid="asset-enrich-panel">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold text-violet-900">Perplexity Asset Enrichment</p>
                {enrich.job?.status === 'running' && (
                  <Badge className="bg-violet-600 text-white text-[10px]">Running</Badge>
                )}
                {enrich.job?.status === 'queued' && (
                  <Badge className="bg-slate-500 text-white text-[10px]">Queued</Badge>
                )}
                {enrich.job?.status === 'done' && (
                  <Badge className="bg-emerald-600 text-white text-[10px]">Done</Badge>
                )}
                {enrich.job?.status === 'cancelled' && (
                  <Badge className="bg-rose-500 text-white text-[10px]">Cancelled</Badge>
                )}
              </div>
              <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">
                Live-search the web to fill <span className="font-medium">MoA</span>,{' '}
                <span className="font-medium">differentiation</span>,{' '}
                <span className="font-medium">IP status</span>, and{' '}
                <span className="font-medium">next clinical milestone</span> on clinical-stage assets
                where these fields are thin. Existing data is never overwritten.
              </p>
              <p className="text-[11px] text-violet-700 mt-2">
                <span className="font-semibold">{enrich.qualifying ?? 0}</span> of{' '}
                <span className="font-semibold">{enrich.total ?? 0}</span> committed assets qualify
                (clinical-stage with ≥2 missing fields).
                {enrich.job?.status === 'done' && enrich.job?.enriched != null && (
                  <span className="ml-2 text-emerald-700">
                    · Last run: {enrich.job.enriched} enriched, {enrich.job.failed || 0} failed.
                  </span>
                )}
                {(enrich.job?.status === 'running' || enrich.job?.status === 'queued') && (
                  <span className="ml-2 text-violet-700">
                    · Progress: {enrich.job.enriched || 0} / {enrich.job.total || enrich.qualifying} done
                    {enrich.job.failed ? ` · ${enrich.job.failed} failed` : ''}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {(enrich.job?.status === 'running' || enrich.job?.status === 'queued') ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={cancelEnrich}
                  data-testid="enrich-cancel-btn"
                >
                  <X className="w-3 h-3 mr-1" /> Cancel
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={kickEnrich}
                  disabled={enrichKicking || (enrich.qualifying ?? 0) === 0}
                  className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
                  data-testid="enrich-kick-btn"
                >
                  {enrichKicking ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  Enrich {enrich.qualifying ?? 0} assets
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filter chips */}
      <div className="mt-5 flex items-center gap-2 flex-wrap">
        {[
          ['all', 'All', data.total],
          ['matched', 'Matched', data.matched],
          ['pharma_promote', '🔄 Promote to Biotech', data.pharma_promote || 0],
          ['unmatched', 'Unmatched', data.unmatched],
          ['stage_changes', 'Stage Δ', data.stage_changes],
        ].map(([k, l, n]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`text-xs px-3 py-1 rounded-full border transition ${
              filter === k
                ? (k === 'pharma_promote'
                    ? 'bg-violet-100 text-violet-800 border-violet-300 font-semibold'
                    : 'bg-emerald-100 text-emerald-800 border-emerald-200 font-semibold')
                : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
            }`}
            data-testid={`asset-filter-${k}`}
            title={
              k === 'pharma_promote'
                ? 'Asset’s company is currently in the Pharma list. Committing this row will move it to Biotech (rewiring outreach history) and then attach the asset.'
                : undefined
            }
          >
            {l} ({n})
          </button>
        ))}
        <div className="flex-1" />
        <Input placeholder="Search asset or company…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64 h-9" data-testid="asset-import-search" />
        {(data.unmatched || 0) > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={rematchUnmatched}
            disabled={rematching}
            data-testid="rematch-unmatched-btn"
            title="Re-runs biotech name-match on unmatched rows using corporate-suffix canonicalization (Ltd / Inc / Biopharm / Co. Ltd). Rescues rows the initial pipeline missed."
          >
            {rematching
              ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Re-matching…</>
              : <><Sparkles className="w-3 h-3 mr-1" /> Re-match unmatched ({data.unmatched})</>}
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={approveAll} data-testid="bulk-approve-matched">
          <CheckCircle2 className="w-3 h-3 mr-1" /> Approve all matched
        </Button>
        <Button onClick={commit} disabled={committing || data.approved === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="asset-commit-btn">
          {committing ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Committing…</> : <><Save className="w-3 h-3 mr-1" /> Commit {data.approved}</>}
        </Button>
      </div>

      {/* Rows */}
      <div className="mt-4 bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2.5 text-left w-10"></th>
              <th className="px-3 py-2.5 text-left">Asset</th>
              <th className="px-3 py-2.5 text-left">Company</th>
              <th className="px-3 py-2.5 text-left">Match</th>
              <th className="px-3 py-2.5 text-left">Phase (source → CRM)</th>
              <th className="px-3 py-2.5 text-left">Indication</th>
              <th className="px-3 py-2.5 text-left">Modality</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No rows.</td></tr>
            )}
            {/* Feb 2026 v3 · render the full filtered slice, not just
                the first 500. User asked to see all assets down the
                page. React handles ~10k rows via .map fine; if perf
                becomes an issue at higher counts, wire up
                react-window virtualization here. */}
            {items.map(r => (
              <tr key={r.id} className={`hover:bg-slate-50/50 ${r.committed ? 'opacity-50' : ''}`}>
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={!!r.approved}
                    onChange={(e) => toggleRow(r.id, e.target.checked)}
                    disabled={r.match_status !== 'matched' || r.committed}
                    data-testid={`approve-${r.id}`}
                    title={r.match_status !== 'matched' ? 'Cannot approve unmatched rows' : ''}
                  />
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900 truncate max-w-[300px]">{r.asset_name}</div>
                  {r.mechanism && <div className="text-[10px] text-slate-400 truncate max-w-[300px]">{r.mechanism}</div>}
                </td>
                <td className="px-3 py-2 text-slate-700">{r.company_name}</td>
                <td className="px-3 py-2">
                  {r.match_status === 'matched' ? (
                    <div>
                      <span className="text-emerald-700">✓ {r.biotech_name_matched}</span>
                      <span className="text-[10px] text-slate-400 ml-1">({r.match_score})</span>
                    </div>
                  ) : (
                    <span className="text-rose-600 text-[11px]"><AlertCircle className="w-3 h-3 inline mr-0.5" /> Not in CRM</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {r.raw_stage ? (
                    <div className="flex items-center gap-1 text-[11px]">
                      <span className="text-slate-500">{r.raw_stage}</span>
                      <span className="text-slate-300">→</span>
                      <span className={`font-medium ${r.stage_changed ? 'text-amber-700' : 'text-slate-700'}`}>{r.canonical_stage || '?'}</span>
                      {r.existing_asset_stage && (
                        <span className="text-[10px] text-slate-400">(was {r.existing_asset_stage})</span>
                      )}
                    </div>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 text-slate-600 truncate max-w-[200px]">{r.indication}</td>
                <td className="px-3 py-2 text-slate-600 truncate max-w-[140px]">{r.modality}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length > 500 && (
          <div className="px-4 py-2 bg-slate-50 text-[11px] text-slate-500 border-t">
            Showing first 500 of {items.length} — use Download buttons for the full slice.
          </div>
        )}
      </div>
    </div>
  );
}



// ────────────────────────────────────────────────────────────────────
// ResumeMatchingButton — re-fires `_match_pipeline` for any rows still
// in `pending`. Needed when the background task died (pod restart) and
// a chunk of rows are stranded with `match_status = pending`. Safe to
// click any time; idempotent on the backend.
// ────────────────────────────────────────────────────────────────────
function ResumeMatchingButton({ importTag, pending, onResumed }) {
  const [running, setRunning] = useState(false);
  const resume = async () => {
    setRunning(true);
    try {
      const r = await axios.post(
        `${API}/asset-imports/${importTag}/resume-matching`,
        {},
        { withCredentials: true },
      );
      if (r.data?.resumed) {
        toast.success(`Resumed — ${r.data.pending_to_process} pending row(s) being re-matched`);
      } else {
        toast.info(r.data?.reason || 'Nothing to resume');
      }
      if (onResumed) await onResumed();
    } catch (e) {
      toast.error('Resume failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={resume}
      disabled={running || !pending}
      className="h-7 text-[11px] shrink-0"
      data-testid="resume-matching-btn"
      title={pending ? `Resume matching ${pending} pending row(s)` : 'No pending rows to resume'}
    >
      {running ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
      Resume matching{pending ? ` (${pending})` : ''}
    </Button>
  );
}
